import { Mui } from "@osu/react-ui";
import { Fragment } from "react";
import { BOOL, CATEGORY_P, FINAL_EXAM_TYPE } from "../../util/constants";
import { dropdownSubtypes, inputMappings } from "../../util/enums";

export const formSections = [
  {
    title: "Graduation Information",
    id: "form-information",
    questions: [
      {
        title: "Graduation Term",
        id: "term",
        required: true,
        type: inputMappings.dropDown,
        subtype: dropdownSubtypes.term,
        dataField: "termCode",
        filter: {
          showFromPresent: 2,
          showHistorical: 1
        }
      },
      {
        title: "Graduation Level",
        id: "graduation-information-level",
        dataField: "gradLevel",
        required: true,
        type: inputMappings.dropDown,
        branch: "graduation-information-plan",
        subtype: dropdownSubtypes.finalExamLevels,
      },
      {
        title: "Graduation Plan",
        id: "graduation-information-plan",
        required: true,
        type: inputMappings.dropDown,
        subtype: dropdownSubtypes.graduatePrograms,
        dataField: "academicPlan",
        filter: [
          {
            field: "graduation-information-level",
            match: "gradLevel",
            operation: "includes"
          }
        ],
        results: [
          {
            label: "Graduation Degree",
            field: "degree",
            dataField: "gradDegree"
          }
        ],
        dependencyHelperText: "Graduation Level must be selected before choosing a Graduation Plan",
        dependencies: ["graduation-information-level"]
      },
      {
        title: "Final Examination Type",
        id: "final-exam-type",
        required: true,
        type: inputMappings.dropDown,
        dataField: "finalExamType",
        choices: [
          {
            id: "first-final-exam",
            title: "First Final Exam",
            value: FINAL_EXAM_TYPE.FIRST_FINAL_EXAM,
            alter: {
              sectionId: "form-information",
              questionId: "request-rep",
              dataField: "requestGradFacultyRep",
              value: BOOL.NO
            }
          },
          {
            id: "second-final-exam",
            title: "Second Final Exam",
            value: FINAL_EXAM_TYPE.SECOND_FINAL_EXAM,
            alter: {
              sectionId: "form-information",
              questionId: "request-rep",
              dataField: "requestGradFacultyRep",
              value: BOOL.YES,
              disabled: true
            }
          }
        ]
      },
      {
        id: "request-rep",
        type: inputMappings.checkbox,
        dataField: "requestGradFacultyRep",
        defaultValue: BOOL.NO,
        choices: [
          {
            id: "grad-faculty-rep",
            value: BOOL.YES,
            title: "I would like to request a Graduate Faculty representative (Required for Second Final Exam)."
          }
        ]
      },
      {
        id: "format-review-complete",
        type: inputMappings.checkbox,
        dataField: "formatReviewComplete",
        defaultValue: BOOL.NO,
        hidden: true,
        choices: [
          {
            id: "format-review-complete-yes",
            title: "Format Review Complete",
            value: BOOL.YES
          }
        ]
      }
    ]
  },
  {
    id: "draft-dissertation-dma",
    title: "Draft Dissertation/D.M.A.",
    summary: (
      <Fragment>
        <Mui.Typography variant="body2">
          A complete draft copy of your dissertation must be submitted to the Graduate School 
          for a format review at least two weeks before the examination date. Your exam cannot 
          be approved to go forward until the format review has been completed.  If a Graduate 
          Faculty Representative is requested (required on second attempts) the student will be 
          responsible for providing a copy of the dissertation to the Grad Rep once notified of 
          the assignment.
        </Mui.Typography>
      </Fragment>
    ),
    questions: [
      {
        id: "draft-dissertation-title",
        title: "Draft Dissertation Title",
        type: inputMappings.textInput,
        dataField: "draftDissertationTitle",
        required: true,
        inputProps: {
          maxLength: 255
        }
      }
    ]
  },
  {
    id: "oral-portion",
    title: "Oral portion of final examination",
    summary: (
      <Fragment>
        <Mui.Typography variant="body2" className="margin-bottom-2">
          Please note that if you will conduct a presentation of your
          dissertation research before the final oral examination, only indicate
          the time frame of the actual defense on this form.
        </Mui.Typography>
        <Mui.Typography variant="body2" className="margin-bottom-2">
          The final oral examination must be on a University business day and
          must begin no earlier than 7:30 AM, but end no later than 5:30 PM, and
          must be two hours in length.
        </Mui.Typography>
      </Fragment>
    ),
    questions: [
      {
        id: "date-input-group",
        title: "Date of Oral Examination",
        type: inputMappings.datetime,
        dataField: "oralDate",
        required: true,
        startTime: true,
        endTime: true,
        validation: {
          begin: "7:30 AM",
          days: 14,
          end: "5:30 PM",
          exactDurationInHours: 2,
          weekend: true
        }
      },
      {
        id: "video-conf-component",
        type: inputMappings.checkbox,
        defaultValue: BOOL.NO,
        dataField: "videoConference",
        choices: [
          {
            id: "yes-video-conf-component",
            value: BOOL.YES,
            title: "There will be a video conference component to this examination.",
            branch: "video-conf-platform"
          }
        ]
      },
      {
        id: "video-conf-platform",
        title: "Video Conference Platform (example: Zoom meeting, Microsoft Teams, Skype, etc.)",
        dependencies: ["video-conf-component"],
        dataField: "videoConferencePlatform",
        type: inputMappings.textInput,
        required: true,
        summary: (
          <Fragment>
            <Mui.Typography variant="body2" className="margin-bottom-2">For examinations that are entirely virtual you do not need to include room and building.</Mui.Typography>
            <Mui.Typography variant="body2">If your examination is both virtual and in person please include room and building.</Mui.Typography>
          </Fragment>
        ),
        inputProps: {
          maxLength: 255
        }
      },
      {
        id: "room-number-building",
        type: inputMappings.inputGroup,
        numberOfResponsesMaximum: 1,
        questions: [
          {
            id: "room-num",
            title: "Room Number",
            dataField: "roomNumber",
            type: inputMappings.textInput,
            inputProps: {
              maxLength: 255
            }
          },
          {
            id: "building",
            title: "Building",
            dataField: "building",
            type: inputMappings.textInput,
            inputProps: {
              maxLength: 255
            }
          }
        ]
      }
    ]
  },
  {
    title: "Committee",
    id: "committee",
    questions: [
      {
        id: "advisor-subheading",
        type: inputMappings.subheading,
        title: "Advisor Information"
      },
      {
        id: "committee-advisor",
        type: inputMappings.dropDown,
        dataField: "committee.advisor",
        title: "Advisor",
        summary: null,
        required: true,
        subtype: dropdownSubtypes.advisor,
        dependencies: ["graduation-information-plan"],
        dependencyHelperText: "Graduation Plan must be selected before choosing an Advisor"
      },
      {
        id: "co-advisor",
        type: inputMappings.dropDown,
        title: "Co-Advisor (if applicable)",
        subtype: dropdownSubtypes.coAdvisor,
        summary: null,
        required: false,
        dataField: "committee.coAdvisor"
      },
      {
        id: "committee-members",
        type: inputMappings.inputGroup,
        title: "Committee Members",
        subtitle: "Graduate Faculty committee members",
        summary: "(M)aster’s level faculty selection will require approval of the Graduate Studies Committee Chair.",
        required: true,
        numberOfResponsesMinimum: 1,
        numberOfResponsesMaximum: 10,
        dependencies: [],
        choices: [],
        dataField: "committee.members",
        questions: [
          {
            id: "committee-member-info",
            type: inputMappings.dropDown,
            subtype: dropdownSubtypes.facultyMember,
            title: "Faculty Member",
            summary: null,
            required: true
          }
        ]
      },
      {
        id: "external-members",
        type: inputMappings.inputGroup,
        title: "External Members",
        subtitle: "Non-Graduate Faculty committee members",
        summary: "For each external committee member, completion & submission of Committee and Examination Petition is required.",
        required: false,
        numberOfResponsesMinimum: 1,
        numberOfResponsesMaximum: 4,
        dependencies: [],
        choices: [],
        dataField: "committee.externalMembers",
        questions: [
          {
            id: "first-name",
            type: inputMappings.textInput,
            title: "First Name",
            dataField: 'firstName',
            summary: null,
            required: true,
            numberOfResponsesMinimum: 1,
            numberOfResponsesMaximum: 1,
            dependencies: [],
            choices: []
          },
          {
            id: "last-name",
            type: inputMappings.textInput,
            title: "Last Name",
            summary: null,
            required: true,
            dataField: 'lastName',
            numberOfResponsesMinimum: 1,
            numberOfResponsesMaximum: 1,
            dependencies: [],
            choices: []
          },
          {
            id: "email",
            type: inputMappings.email,
            title: "Email Address",
            dataField: "email",
            summary: null,
            required: true,
            numberOfResponsesMinimum: 1,
            numberOfResponsesMaximum: 1,
            dependencies: [],
            choices: []
          }
        ]
      },
      {
        id: "grad-faculty-rep",
        type: inputMappings.dropDown,
        subtype: dropdownSubtypes.gradFacultyRepresentative,
        dataField: "committee.gradFacultyRepresentative",
        title: "Graduate Faculty Representative",
        required: false,
        hidden: true
      },
      {
        id: "category-m-approved",
        type: inputMappings.checkbox,
        dataField: "categoryMApproved",
        defaultValue: BOOL.NO,
        hidden: true,
        choices: [
          {
            id: "category-m-approved-yes",
            title: "Category M Approved",
            value: BOOL.YES
          }
        ]
      }
    ]
  }
];

export const gradChairApprovalQuestions = [
  {
    id: "category-m-approved",
    type: inputMappings.checkbox,
    dataField: "categoryMApproved",
    choices: [
      {
        id: "category-m-approved-yes",
        value: BOOL.YES,
        title: "Category M faculty approved"
      }
    ],
    validation: (form, value) => (value !== BOOL.YES ? "Category M Approval is required." : null)
  }
];

export const gradSchoolApprovalQuestions = [
  {
    id: "grad-faculty-rep",
    type: inputMappings.dropDown,
    subtype: dropdownSubtypes.gradFacultyRepresentative,
    dataField: "committee.gradFacultyRepresentative",
    title: "Graduate Faculty Representative",
    className: "margin-bottom-0",
    fullWidth: true,
    filterState: {
      category: {
        dataField: "category",
        value: {
          category: CATEGORY_P
        }
      }
    },
    validation: (form, value) => ((form?.requestGradFacultyRep === BOOL.YES && !value) ? "Graduate Faculty Representative is required." : null)
  },
  {
    id: "format-review-complete",
    type: inputMappings.checkbox,
    dataField: "formatReviewComplete",
    choices: [
      {
        id: "format-review-complete-yes",
        value: BOOL.YES,
        title: "Format Review Complete",
      }
    ]
  }
];

export const finalApprovalQuestions = [
  {
    id: "grad-faculty-rep",
    type: inputMappings.dropDown,
    subtype: dropdownSubtypes.gradFacultyRepresentative,
    dataField: "committee.gradFacultyRepresentative",
    title: "Graduate Faculty Representative",
    className: "margin-bottom-0",
    fullWidth: true,
    filterState: {
      category: {
        dataField: "category",
        value: {
          category: CATEGORY_P
        }
      }
    },
    validation: (form, value) => ((form?.requestGradFacultyRep === BOOL.YES && !value) ? "Graduate Faculty Representative is required." : null)
  },
  {
    id: "format-review-complete",
    type: inputMappings.checkbox,
    dataField: "formatReviewComplete",
    choices: [
      {
        id: "format-review-complete-yes",
        value: BOOL.YES,
        title: "Format Review Complete",
      }
    ]
  }
];

export const additionalFilters = [
  {
    id: "oral-date-after",
    title: "Oral Date After",
    type: inputMappings.date,
    startTime: false,
    endTime: false
  },
  {
    id: "oral-date-before",
    title: "Oral Date Before",
    type: inputMappings.date,
    startTime: false,
    endTime: false
  },
  {
    id: "exam-type",
    title: "Exam Type",
    required: false,
    type: inputMappings.dropDown,
    subtype: dropdownSubtypes.examType
  },
  {
    id: "category-m",
    title: "Category M",
    required: false,
    type: inputMappings.dropDown,
    choices: [
      {
        id: "all-category-m",
        title: "All",
        value: "ALL"
      },
      {
        id: "yes-category-m",
        title: "Yes",
        value: "Y"
      },
      {
        id: "no-category-m",
        title: "No",
        value: "N"
      }
    ]
  },
  {
    id: "format-review-complete",
    title: "Format Review Complete",
    required: false,
    type: inputMappings.dropDown,
    choices: [
      {
        id: "all-format-review-complete",
        title: "All",
        value: "ALL"
      },
      {
        id: "yes-format-review-complete",
        title: "Yes",
        value: "Y"
      },
      {
        id: "no-format-review-complete",
        title: "No",
        value: "N"
      }
    ]
  },
  {
    title: "Graduation Level",
    id: "level",
    required: true,
    type: inputMappings.dropDown,
    subtype: dropdownSubtypes.graduationLevel, 
    dataField: 'gradLevel',
  },
  {
    title: "Graduation Degree",
    id: "degree",
    required: true,
    type: inputMappings.dropDown,
    subtype: dropdownSubtypes.degrees,
    dataField: 'degree'
  }
];